// 8x8 to 12x12 pixel icons as string grids, rendered as crisp SVG rects (one rect per run).
import { memo } from 'react';
import type { Grid, Palette } from '../../pixel/engine';

const PAL: Palette = {
  k: '#05040A',
  i: '#F2EAD8',
  I: '#A99EC0',
  g: '#6E6886',
  G: '#4A4560',
  d: '#2A2440',
  e: '#FF7A2F',
  E: '#B8441C',
  m: '#FFC247',
  w: '#FFF6D8',
  b: '#8C5A34',
  B: '#5A3A22',
  q: '#7FE0FF',
  s: '#E0506A',
  '1': '#EFE3B8',
  '2': '#D9B85C',
  '3': '#B7773A',
  '4': '#8A4B78',
  '5': '#4C63B6',
};

const ICONS = {
  hammer: [
    '.kkkkkkkk...',
    'kIIIIIIIgk..',
    'kgggggggGk..',
    '.kkkkkkkk...',
    '....kbk.....',
    '....kbk.....',
    '....kbk.....',
    '....kbk.....',
    '....kbk.....',
    '....kBk.....',
    '....kBk.....',
    '.....k......',
  ],
  anvil: [
    '............',
    '............',
    'kkkkkkkkkkkk',
    'kiiiiiiiiiik',
    '.kIggggggggk',
    '..kkkggggkk.',
    '.....kggk...',
    '.....kggk...',
    '....kggggk..',
    '...kggggggk.',
    '...kkkkkkkk.',
    '............',
  ],
  ingot: ['..kkkkkkkk..', '.kwwwwwwwwk.', '.kmmmmmmmek.', 'kmmmmmmmmmek', 'keeeeeeeeeEk', 'kkkkkkkkkkkk'],
  flame: [
    '....k.....',
    '...kek....',
    '...kek..k.',
    '..kemek.kk',
    '.kemmmekek',
    '.kemwmmeek',
    'kemwwwmmek',
    'kemwwwwmek',
    'kemwwwwmek',
    '.kemwwmek.',
    '..kemmek..',
    '...kkkk...',
  ],
  spark: ['....m....', '....m....', '...mwm...', 'mmmwwwmmm', '...mwm...', '....m....', '....m....'],
  wallet: [
    '..kkkkkkkk..',
    '.kiiiiiiik..',
    'kkkkkkkkkkkk',
    'kbbbbbbbbbbk',
    'kbbbbbbbkkkk',
    'kbbbbbbkmmmk',
    'kbbbbbbkmwmk',
    'kbbbbbbbkkkk',
    'kBBBBBBBBBBk',
    'kkkkkkkkkkkk',
  ],
  pool: [
    'k..........k',
    'kkkkkkkkkkkk',
    'kgmwmmmmwmgk',
    'kgmmmmmmmmgk',
    '.kgeeeeeegk.',
    '.kgGGGGGGgk.',
    '..kgGGGGgk..',
    '..kkkkkkkk..',
  ],
  curve: [
    '..........kk',
    '..........5k',
    '........kk5k',
    '........445k',
    '......kk445k',
    '......33445k',
    '....kk33445k',
    '....2233445k',
    '..kk2233445k',
    '..112233445k',
    'kk112233445k',
    'kkkkkkkkkkkk',
  ],
  chip: [
    '..k.k.k.k...',
    '.kkkkkkkkk..',
    'kkdddddddkk.',
    '.kdqqqqqdk..',
    'kkdqwwqqdkk.',
    '.kdqqqqqdk..',
    'kkdddddddkk.',
    '.kkkkkkkkk..',
    '..k.k.k.k...',
  ],
  stamp: ['..kkkk..', '..kssk..', '..kssk..', '.kkkkkk.', 'kssssssk', 'kkkkkkkk', '.kiiiik.', '..kkkk..'],
  arrow: ['...k....', '...kk...', 'kkkkmk..', 'kmmmmmk.', 'kmmmmmk.', 'kkkkmk..', '...kk...', '...k....'],
} satisfies Record<string, Grid>;

export type IconName = keyof typeof ICONS;

function runs(grid: Grid): { x: number; y: number; w: number; c: string }[] {
  const out: { x: number; y: number; w: number; c: string }[] = [];
  grid.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      const ch = row[x]!;
      if (ch === '.' || ch === ' ') {
        x++;
        continue;
      }
      let e = x + 1;
      while (e < row.length && row[e] === ch) e++;
      const c = PAL[ch];
      if (c) out.push({ x, y, w: e - x, c });
      x = e;
    }
  });
  return out;
}

const RUNS = Object.fromEntries(Object.entries(ICONS).map(([k, g]) => [k, { runs: runs(g), w: Math.max(...g.map((r) => r.length)), h: g.length }])) as Record<
  IconName,
  { runs: ReturnType<typeof runs>; w: number; h: number }
>;

/** A pixel icon; `px` is the size of one sprite pixel in CSS pixels. */
export const PixelIcon = memo(function PixelIcon({ name, px = 2, className, title }: { name: IconName; px?: number; className?: string; title?: string }) {
  const r = RUNS[name];
  return (
    <svg
      className={className}
      width={r.w * px}
      height={r.h * px}
      viewBox={`0 0 ${r.w} ${r.h}`}
      shapeRendering="crispEdges"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      style={{ flex: 'none', display: 'inline-block', verticalAlign: 'middle' }}
    >
      {title ? <title>{title}</title> : null}
      {r.runs.map((q, i) => (
        <rect key={i} x={q.x} y={q.y} width={q.w} height={1} fill={q.c} />
      ))}
    </svg>
  );
});
